import { useEffect, startTransition, useRef, useCallback } from 'react';
import { useBreadcrumbsContext } from '@/creditflowresearch/ui/common/components/header/Breadcrumbs/BreadcrumbsContext';
import { useMounted } from './useMounted';
import IBreadcrumb from '@/creditflowresearch/domain/vo/Breadcrumbs';
import isEqual from 'lodash/isEqual';

export const useBreadcrumbUpdates = () => {
  const { setBreadcrumbs, breadcrumbs: currentBreadcrumbs } = useBreadcrumbsContext();
  const isMounted = useMounted();

  const updateBreadcrumbs = useCallback((breadcrumbs: IBreadcrumb[] | null) => {
    if (!isMounted) {
      return;
    }

    if (isEqual(currentBreadcrumbs, breadcrumbs)) {
      return;
    }

    startTransition(() => {
      setBreadcrumbs(breadcrumbs);
    });
  }, [isMounted, currentBreadcrumbs, setBreadcrumbs]);

  return {
    updateBreadcrumbs,
    isMounted,
    setBreadcrumbs,
  };
};

export const useBreadcrumbs = (
  breadcrumbs: IBreadcrumb[] | null | (() => IBreadcrumb[] | null),
  dependencies: React.DependencyList,
) => {
  const { updateBreadcrumbs, isMounted } = useBreadcrumbUpdates();
  const previousBreadcrumbsRef = useRef<IBreadcrumb[] | null>(null);

  useEffect(() => {
    if (!isMounted) {
      return;
    }

    const breadcrumbsValue = typeof breadcrumbs === 'function' ? breadcrumbs() : breadcrumbs;

    if (!isEqual(previousBreadcrumbsRef.current, breadcrumbsValue)) {
      previousBreadcrumbsRef.current = breadcrumbsValue;
      updateBreadcrumbs(breadcrumbsValue);
    }
  }, [isMounted, updateBreadcrumbs, ...dependencies]);
};
